<template>
  <FormCard
    :title="$t('otpVerification.title')"
    :subtitle="$t(authMethod === 'email' ? 'otpVerification.subtitleEmail' : 'otpVerification.subtitlePhone', { contact: contactValue })"
    icon
    iconBgClass="bg-gradient-to-br from-green-500 to-emerald-600"
  >
    <template #icon>
      <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
      </svg>
    </template>

    <!-- Contact display -->
    <div class="text-center mb-4">
      <span class="font-semibold text-blue-600">{{ contactValue }}</span>
    </div>
    
    <!-- Countdown Timer -->
    <div v-if="otpCountdown > 0" class="flex justify-center mb-4">
      <div class="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full border border-blue-200">
        <svg class="w-4 h-4 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span class="text-xs font-medium text-blue-700">{{ $t('otpVerification.codeExpires', { time: formatTime(otpCountdown) }) }}</span>
      </div>
    </div>

    <form @submit.prevent="$emit('submit')" class="space-y-4" novalidate>
      <div class="form-group">
        <label class="form-label flex flex-col items-center justify-center text-center block mb-4">
          <div class="flex items-center justify-center gap-1 mb-1">
            <div class="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <span class="text-base font-semibold">{{ $t('otpVerification.codeLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        
        <!-- OTP Input Grid -->
        <div class="flex justify-center items-center gap-2 sm:gap-3">
          <input
            v-for="index in 6"
            :key="index"
            :ref="el => { if (el) otpRefs[index - 1] = el }"
            type="text"
            inputmode="numeric"
            pattern="[0-9]"
            maxlength="1"
            :value="otpDigits[index - 1]"
            @input="handleOtpInput($event, index - 1)"
            @keydown="handleOtpKeydown($event, index - 1)"
            @paste="handleOtpPaste"
            @focus="focusedIndex = index - 1"
            class="otp-input"
            :class="{
              'border-blue-500 bg-blue-50': otpDigits[index - 1] || focusedIndex === index - 1,
              'border-red-500 bg-red-50': error && !otpDigits[index - 1]
            }"
            autocomplete="off"
            :aria-label="$t('otpVerification.digitAria', { index })"
          />
        </div>
        
        <div v-if="error" class="form-error text-center mt-2" role="alert">
          <svg class="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ error }}
        </div>
      </div>

      <!-- Resend OTP -->
      <div class="text-center pt-1">
        <div class="inline-flex items-center gap-1 text-xs">
          <svg class="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span v-if="!canResendOTP" class="text-gray-600">
            {{ $t('otpVerification.resendIn', { time: formatTime(resendCountdown) }) }}
          </span>
          <button
            v-else
            @click.prevent="$emit('resend')"
            type="button"
            class="text-blue-600 hover:text-blue-700 font-medium transition-colors"
            :disabled="resendLoading"
          >
            <svg v-if="resendLoading" class="inline w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            {{ resendLoading ? $t('otpVerification.resendingBtn') : $t('otpVerification.resendBtn') }}
          </button>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-center gap-3 pt-4">
        <button 
          type="button" 
          @click="$emit('back')" 
          class="btn-secondary group text-sm py-2"
        >
          <svg class="w-4 h-4 mr-1 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          {{ $t('otpVerification.backBtn') }}
        </button>
        
        <button 
          type="submit" 
          class="btn-primary group text-sm py-2"
          :disabled="loading || otpDigits.join('').length !== 6"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span>{{ loading ? $t('otpVerification.verifyingBtn') : $t('otpVerification.verifyBtn') }}</span>
          <svg v-if="!loading" class="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>
    </form>
  </FormCard>
</template>

<script>
import FormCard from '../ui/FormCard.vue';

export default {
  name: 'OTPVerification',
  components: { FormCard },
  props: {
    authMethod: { type: String, required: true },
    contactValue: { type: String, required: true },
    otpCountdown: { type: Number, default: 0 },
    resendCountdown: { type: Number, default: 0 },
    canResendOTP: { type: Boolean, default: false },
    resendLoading: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' }
  },
  emits: ['submit', 'back', 'resend', 'update:otp'],
  data() {
    return {
      otpDigits: ['', '', '', '', '', ''],
      focusedIndex: 0,
      otpRefs: []
    };
  },
  methods: {
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    
    handleOtpInput(event, index) {
      const value = event.target.value;
      if (!/^\d*$/.test(value)) {
        this.otpDigits[index] = '';
        return;
      }
      this.otpDigits[index] = value;
      if (value && index < 5) {
        this.$nextTick(() => {
          this.otpRefs[index + 1]?.focus();
        });
      }
      this.$emit('update:otp', this.otpDigits.join(''));
    },
    
    handleOtpKeydown(event, index) {
      if (event.key === 'Backspace') {
        if (!this.otpDigits[index] && index > 0) {
          this.otpDigits[index - 1] = '';
          this.$nextTick(() => {
            this.otpRefs[index - 1]?.focus();
          });
        } else {
          this.otpDigits[index] = '';
        }
        this.$emit('update:otp', this.otpDigits.join(''));
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        this.otpRefs[index - 1]?.focus();
      }
      if (event.key === 'ArrowRight' && index < 5) {
        this.otpRefs[index + 1]?.focus();
      }
    },
    
    handleOtpPaste(event) {
      event.preventDefault();
      const pasteData = event.clipboardData.getData('text').trim();
      if (/^\d{6}$/.test(pasteData)) {
        const digits = pasteData.split('');
        digits.forEach((digit, index) => {
          if (index < 6) {
            this.otpDigits[index] = digit;
          }
        });
        this.$emit('update:otp', this.otpDigits.join(''));
        this.$nextTick(() => {
          this.otpRefs[5]?.focus();
        });
      }
    },
    
    resetOtp() {
      this.otpDigits = ['', '', '', '', '', ''];
      this.$emit('update:otp', '');
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.otpRefs[0]?.focus();
    });
  }
}
</script>

<style scoped>
.form-group {
  @apply w-full space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-gray-700;
}

.otp-input {
  width: 2.5rem;
  height: 2.75rem;
  font-size: 1.25rem;
  font-weight: 700;
  text-align: center;
  background: white;
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  transition: all 0.2s;
}

.otp-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  outline: none;
}

@media (min-width: 375px) {
  .otp-input {
    width: 2.75rem;
    height: 3rem;
    font-size: 1.375rem;
  }
}

@media (min-width: 400px) {
  .otp-input {
    width: 3rem;
    height: 3.25rem;
    font-size: 1.5rem;
  }
}

@media (min-width: 640px) {
  .otp-input {
    width: 3.5rem;
    height: 3.5rem;
    font-size: 1.75rem;
  }
}

.form-error {
  @apply text-red-600 text-sm flex items-center justify-center;
}

.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-2.5 px-4 sm:px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
}

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-4 sm:px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
}
</style>